/**
 * Parseur d'une ligne d'ingrédient (§3.2 CLAUDE.md).
 *
 * Règle de repli, non négociable : si une ligne n'est pas parsable, elle est
 * conservée intégralement dans `name`, avec `quantity` et `unit` à null.
 * On ne perd jamais le texte source, on ne devine jamais une quantité.
 *
 * Le repli s'applique dans cinq cas, et le doute profite toujours au texte
 * source plutôt qu'à une donnée structurée plausible :
 *   1. aucun motif de quantité en tête de ligne (« Persil ciselé ») ;
 *   2. fourchette de quantité (« 2 à 3 pommes ») ;
 *   3. article indéfini ne portant pas sur une unité (« Un peu de sel ») ;
 *   4. mot d'unité entamé mais non reconnu (« 2 cuillères bombées de sucre ») ;
 *   5. découpage laissant un nom d'ingrédient vide (« 1 cup »).
 */

import type { Bool01, Unit } from '@mlc/shared';
import { matchLeadingUnit } from './matchLeadingUnit';
import { AMBIGUOUS_UNITS, LIAISON_WORDS, normalizeForMatch, UNIT_ALIAS_HEADS } from './units';

/**
 * Une unité ambiguë (« cuillere », à la fois mesure de maison et début de
 * « cuillère à soupe ») n'est retenue que si un mot de liaison la suit. Sans
 * liaison, le mot qui vient qualifie l'unité — « bombées », « rases » — et non
 * l'ingrédient : la ligne parle d'une mesure qu'on ne sait pas interpréter.
 */
function isAmbiguousWithoutLiaison(match: { unit: Unit; length: number }, rest: string): boolean {
  if (!AMBIGUOUS_UNITS.has(match.unit)) return false;
  const after = rest.slice(match.length).trim();
  if (after.length === 0) return true;
  return !LIAISON_WORDS.has(firstWord(after));
}

export interface ParsedIngredientLine {
  quantity: number | null;
  unit: Unit | null;
  name: string;
  note: string | null;
  is_optional: Bool01;
}

const UNICODE_FRACTIONS: Record<string, number> = {
  '¼': 1 / 4,
  '⅓': 1 / 3,
  '½': 1 / 2,
  '⅔': 2 / 3,
  '¾': 3 / 4,
};
const UNICODE_FRACTION_CHARS = Object.keys(UNICODE_FRACTIONS).join('');

interface QuantityMatch {
  value: number;
  /** Longueur, en caractères, du motif reconnu dans le texte d'origine. */
  length: number;
  /**
   * Vrai si la quantité vient d'un article indéfini (« un »/« une ») et non
   * d'un chiffre. Un article n'est une quantité que s'il porte sur une unité :
   * « Une pincée de piment » vaut 1 pincée, « Un peu de sel » ne vaut rien.
   */
  fromArticle: boolean;
}

/**
 * Reconnaît une quantité en tête de ligne, dans l'ordre du motif le plus
 * spécifique au plus général : nombre mixte avec fraction unicode ou ASCII,
 * fraction seule, nombre décimal, puis article indéfini français
 * (« un »/« une », ex. « Une pincée »).
 */
function extractLeadingQuantity(text: string): QuantityMatch | null {
  const mixedUnicode = new RegExp(`^(\\d+)\\s+([${UNICODE_FRACTION_CHARS}])`).exec(text);
  if (mixedUnicode) {
    const whole = mixedUnicode[1];
    const glyph = mixedUnicode[2];
    if (whole !== undefined && glyph !== undefined) {
      return {
        value: Number(whole) + (UNICODE_FRACTIONS[glyph] ?? 0),
        length: mixedUnicode[0].length,
        fromArticle: false,
      };
    }
  }

  const mixedAscii = /^(\d+)\s+(\d+)\/(\d+)/.exec(text);
  if (mixedAscii) {
    const [whole, num, den] = mixedAscii.slice(1);
    if (whole !== undefined && num !== undefined && den !== undefined) {
      return {
        value: Number(whole) + Number(num) / Number(den),
        length: mixedAscii[0].length,
        fromArticle: false,
      };
    }
  }

  const bareAscii = /^(\d+)\/(\d+)/.exec(text);
  if (bareAscii) {
    const [num, den] = bareAscii.slice(1);
    if (num !== undefined && den !== undefined) {
      return { value: Number(num) / Number(den), length: bareAscii[0].length, fromArticle: false };
    }
  }

  const bareUnicode = new RegExp(`^[${UNICODE_FRACTION_CHARS}]`).exec(text);
  if (bareUnicode) {
    return { value: UNICODE_FRACTIONS[bareUnicode[0]] ?? 0, length: bareUnicode[0].length, fromArticle: false };
  }

  const decimal = /^(\d+(?:[.,]\d+)?)/.exec(text);
  if (decimal?.[1] !== undefined) {
    return { value: Number(decimal[1].replace(',', '.')), length: decimal[0].length, fromArticle: false };
  }

  const article = /^(une|un)\b/i.exec(text);
  if (article) {
    return { value: 1, length: article[0].length, fromArticle: true };
  }

  return null;
}

/**
 * Fourchette de quantité : « 2 à 3 pommes », « 2-3 pommes », « 2 ou 3 pommes ».
 * La ligne porte deux quantités ; retenir la borne basse serait en inventer une.
 */
const RANGE_MARKER = /^(?:(?:à|a|ou)\s+|\s*[-–—]\s*)\d/i;

/** Premier mot du texte, normalisé comme les alias d'unités. */
function firstWord(text: string): string {
  return normalizeForMatch(text.split(/\s+/)[0] ?? '');
}

/** Retire un connecteur (« de », « d' ») en tête de texte, une fois le nombre et l'unité consommés. */
function stripLeadingConnector(text: string): string {
  return text.replace(/^(de\b\s*|d['’]\s*)/i, '').trim();
}

/** Sépare le nom de l'ingrédient de sa note libre, sur la première virgule. */
/**
 * Ponctuation orpheline autour d'un nom d'ingrédient (US-27). Les listes des
 * pages web en laissent régulièrement : un guillemet ouvrant sans fermant, un
 * tiret de puce oublié, un point final. Ce sont des restes de mise en forme,
 * pas des données — les retirer ne perd rien, tandis que les garder produit
 * « "farine » dans la liste de courses.
 *
 * Volontairement limité aux **extrémités** : la ponctuation interne
 * (« farine T55, tamisée ») porte du sens.
 */
const ORPHAN_PUNCTUATION = /^[\s"'“”«»\-–—•*.:;]+|[\s"'“”«»\-–—•*.:;]+$/g;

function trimOrphanPunctuation(text: string): string {
  const cleaned = text.replace(ORPHAN_PUNCTUATION, '').trim();
  // Une ligne qui n'était que ponctuation reste telle quelle : la vider
  // reviendrait à perdre le texte source (§3.2).
  return cleaned.length > 0 ? cleaned : text.trim();
}

function splitNameAndNote(text: string): [string, string | null] {
  const commaIndex = text.indexOf(',');
  if (commaIndex === -1) {
    return [trimOrphanPunctuation(text), null];
  }
  const name = trimOrphanPunctuation(text.slice(0, commaIndex));
  const note = trimOrphanPunctuation(text.slice(commaIndex + 1));
  return [name, note.length > 0 ? note : null];
}

const OPTIONAL_MARKER = /\(\s*facultatif\s*\)/i;

/**
 * Repli du §3.2 : la ligne est conservée intégralement dans `name`, `quantity`
 * et `unit` à null. Aucune information n'est perdue, rien n'est inventé ;
 * l'écran d'édition (§3.4, validation humaine systématique) fera le reste.
 */
function keepWholeLine(original: string): ParsedIngredientLine {
  return { quantity: null, unit: null, name: original, note: null, is_optional: 0 };
}

export function parseIngredientLine(rawLine: string): ParsedIngredientLine {
  const original = rawLine.trim();
  const quantityMatch = extractLeadingQuantity(original);

  // Aucune quantité détectable en tête de ligne.
  if (!quantityMatch) return keepWholeLine(original);

  let rest = original.slice(quantityMatch.length).trim();

  // « 2 à 3 pommes » : deux quantités, on n'en choisit aucune.
  if (RANGE_MARKER.test(rest)) return keepWholeLine(original);

  let isOptional: Bool01 = 0;
  const optionalMatch = OPTIONAL_MARKER.exec(rest);
  if (optionalMatch) {
    isOptional = 1;
    rest = (rest.slice(0, optionalMatch.index) + ' ' + rest.slice(optionalMatch.index + optionalMatch[0].length))
      .replace(/\s+/g, ' ')
      .trim();
  }

  const unitMatch = matchLeadingUnit(rest);
  let unit: Unit;
  if (unitMatch !== null && isAmbiguousWithoutLiaison(unitMatch, rest)) {
    // « 2 cuillères bombées de sucre » : la cuillère de maison existe (US-26),
    // mais « bombées » dit qu'on parlait d'autre chose. Repli du §3.2.
    return keepWholeLine(original);
  }
  if (unitMatch) {
    unit = unitMatch.unit;
    rest = stripLeadingConnector(rest.slice(unitMatch.length).trim());
  } else {
    // Un article n'est une quantité que s'il porte sur une unité : « Un peu de
    // sel », « Un filet d'huile » ne comptent rien.
    if (quantityMatch.fromArticle) return keepWholeLine(original);

    // Mot d'unité entamé mais non reconnu (« 2 cuillères bombées ») : c'est une
    // unité mal écrite, pas un compte de pièces.
    if (UNIT_ALIAS_HEADS.has(firstWord(rest))) return keepWholeLine(original);

    // Un nombre suivi directement d'un nom, sans mot d'unité (« 1/2 oignon »),
    // désigne un compte d'unités entières de l'ingrédient.
    unit = 'piece';
  }

  const [name, note] = splitNameAndNote(rest);

  // Une quantité et une unité sans nom d'ingrédient ne veulent rien dire : la
  // ligne est mal découpée, on la conserve entière plutôt que de l'amputer.
  if (name.length === 0) return keepWholeLine(original);

  return { quantity: quantityMatch.value, unit, name, note, is_optional: isOptional };
}
